import {useWindowSize} from '@wandb/weave/common/hooks/useWindowSize';
import React, {useCallback, useEffect, useState} from 'react';

import {LoaderProxy} from '../../containers/AppLoader';
import {
  INPUT_ACTION,
  useRegisterAction,
  useRegisterScope,
} from '../../inputSystem';
import {trackToggleRunSelector} from '../../services/analytics/runSelectorToggleEvents';
import {FullScreenPanelContainer} from '../Panel/FullScreenPanel/FullScreenPanel';
import {FullScreenPanelNotFound} from '../Panel/FullScreenPanel/FullScreenPanelNotFound';
import {useFullScreenPanelLookup} from '../Panel/FullScreenPanel/useFullScreenPanelLookup';
import {useAutoSyncPanelBankContext} from '../PanelBank/contextProviders/AutoSyncPanelBankContext';
import {PanelBank} from '../PanelBank/PanelBank';
import {RunDetails} from '../RunDetails/RunDetails';
import {useShouldAutoGeneratePanels} from '../WorkspaceDrawer/Settings/hooks';
import {SectionSettingsContextProvider} from '../WorkspaceDrawer/Settings/SectionSettingsContextProvider';
import {WorkspaceModePrompt} from '../WorkspaceModePrompt/WorkspaceModePrompt';
import {SingleRunNotFound, useSingleRunModeContext} from './SingleRunMode';
import {type MultiRunWorkspaceAllProps} from './types';
import {WorkspaceWithRunSelector} from './WorkspaceWithRunSelector';

export type MultiRunWorkspaceContentContainerProps = {
  children?: React.ReactNode;
  entityName: string;
  isProjectReadOnly: boolean;
  isSingleMode: boolean;
  isTableExpanded: boolean;
  onSelectRunName: MultiRunWorkspaceAllProps['onSelectRunName'];
  onSetTableExpanded: MultiRunWorkspaceAllProps['onSetTableExpanded'];
  panelBankWidth: number;
  projectName: string;
  recommendedGrouping: MultiRunWorkspaceAllProps['recommendedGrouping'];
  runSetRef: MultiRunWorkspaceAllProps['runSetRef'];
  sectionPart: MultiRunWorkspaceAllProps['sectionPart'];
  selectedRunName: string | undefined;
  setPanelBankWidth: (width: number) => void;
  showArtifactCounts: boolean;
  showLogCounts: boolean;
  subTitle?: string;
  title: string;
  workspaceID: string;
};

export const MultiRunWorkspaceContentContainer = React.memo(
  ({
    children,
    entityName,
    isProjectReadOnly,
    isSingleMode,
    isTableExpanded,
    onSelectRunName,
    onSetTableExpanded,
    panelBankWidth,
    projectName,
    recommendedGrouping,
    runSetRef,
    sectionPart,
    selectedRunName,
    setPanelBankWidth,
    showArtifactCounts,
    showLogCounts,
    subTitle,
    title,
    workspaceID,
  }: MultiRunWorkspaceContentContainerProps) => {
    useRegisterScope('workspace');
    const {isSingleRun} = useSingleRunModeContext();
    const {isFullScreenPanelPage} = useFullScreenPanelLookup();
    const [isRunSelectorMinimized, setRunSelectorMinimized] = useState(false);

    const [shouldShowRunSelector, setShouldShowRunSelector] = useState(
      !isSingleRun && !isRunSelectorMinimized
    );

    const toggleMinimizeRunSelectorWithTracking = useCallback(
      (method: 'hotkey' | 'button') => {
        setRunSelectorMinimized(prev => !prev);
        const toggleAction = isRunSelectorMinimized
          ? 'Reveal run selector'
          : 'Minimize run selector';
        trackToggleRunSelector({
          action: toggleAction,
          method,
          location: isFullScreenPanelPage
            ? 'full-screen-panel-view'
            : 'workspace',
        });
      },
      [setRunSelectorMinimized, isRunSelectorMinimized, isFullScreenPanelPage]
    );

    const toggleExpandRunsTableWithTracking = useCallback(
      (method: 'hotkey' | 'button') => {
        if (onSetTableExpanded) {
          onSetTableExpanded(!isTableExpanded);
          const toggleAction = isTableExpanded
            ? 'Collapse runs table'
            : 'Expand runs table';
          trackToggleRunSelector({
            action: toggleAction,
            method,
            location: isTableExpanded ? 'runs-table' : 'workspace',
          });
        }
      },
      [onSetTableExpanded, isTableExpanded]
    );

    const handleToggleMinimizeRunSelectorShortcut = useCallback(() => {
      if (!isTableExpanded) {
        toggleMinimizeRunSelectorWithTracking('hotkey');
      }
    }, [isTableExpanded, toggleMinimizeRunSelectorWithTracking]);

    const handleToggleRunsSidebarShortcut = useCallback(() => {
      toggleExpandRunsTableWithTracking('hotkey');
    }, [toggleExpandRunsTableWithTracking]);

    useRegisterAction(
      INPUT_ACTION.MINIMIZE_RUN_SELECTOR,
      handleToggleMinimizeRunSelectorShortcut
    );
    useRegisterAction(
      INPUT_ACTION.EXPAND_COLLAPSE_RUNS_SIDEBAR,
      handleToggleRunsSidebarShortcut
    );

    const shouldAutoGeneratePanels = useShouldAutoGeneratePanels();

    if (shouldAutoGeneratePanels === 'pending') {
      return <WorkspaceModePrompt />;
    }

    return (
      <WorkspaceWithRunSelector
        isProjectReadOnly={isProjectReadOnly}
        isSingleMode={isSingleMode}
        isTableExpanded={isTableExpanded}
        toggleMinimizeRunSelectorWithTracking={
          toggleMinimizeRunSelectorWithTracking
        }
        onSelectRunName={onSelectRunName}
        onSetTableExpanded={onSetTableExpanded}
        panelBankWidth={panelBankWidth}
        recommendedGrouping={recommendedGrouping}
        runSetRef={runSetRef}
        sectionPart={sectionPart}
        selectedRunName={selectedRunName}
        setPanelBankWidth={setPanelBankWidth}
        shouldShowRunSelector={shouldShowRunSelector}
        showArtifactCounts={showArtifactCounts}
        showLogCounts={showLogCounts}
        subTitle={subTitle}
        title={title}
        toggleExpandRunsTableWithTracking={toggleExpandRunsTableWithTracking}>
        <WorkspaceContent
          entityName={entityName}
          isProjectReadOnly={isProjectReadOnly}
          isTableExpanded={isTableExpanded}
          projectName={projectName}
          workspaceID={workspaceID}
          setShouldShowRunSelector={setShouldShowRunSelector}
          isRunSelectorMinimized={isRunSelectorMinimized}
          toggleMinimizeRunSelectorWithTracking={
            toggleMinimizeRunSelectorWithTracking
          }>
          {children}
        </WorkspaceContent>
      </WorkspaceWithRunSelector>
    );
  }
);

export const WorkspaceContent = ({
  children,
  entityName,
  isProjectReadOnly,
  isTableExpanded,
  projectName,
  workspaceID,
  setShouldShowRunSelector,
  isRunSelectorMinimized,
  toggleMinimizeRunSelectorWithTracking,
}: {
  children?: React.ReactNode;
  entityName: string;
  isProjectReadOnly: boolean;
  isTableExpanded: boolean;
  projectName: string;
  workspaceID: string;
  setShouldShowRunSelector: (shouldShowRunSelector: boolean) => void;
  isRunSelectorMinimized: boolean;
  toggleMinimizeRunSelectorWithTracking: (method: 'hotkey' | 'button') => void;
}) => {
  const {responsiveSize} = useWindowSize();
  const {isLoading: isAutoSyncingPanelBank} = useAutoSyncPanelBankContext();
  const {
    isSingleRun,
    isLoading: isLoadingSingleRun,
    run,
  } = useSingleRunModeContext();
  const isLoading = isLoadingSingleRun || isAutoSyncingPanelBank;

  const {isFullScreenPanelPage, panelRefAndPanelBankSectionConfigRef} =
    useFullScreenPanelLookup();
  const isFullScreenPanelNotFound =
    !isLoading &&
    isFullScreenPanelPage &&
    !panelRefAndPanelBankSectionConfigRef;
  const isSingleRunNotFound = isSingleRun && !isLoading && !run;
  const isMobileWorkspaceTab = !isTableExpanded && responsiveSize !== 'large';

  useEffect(() => {
    if (
      isFullScreenPanelNotFound ||
      isMobileWorkspaceTab ||
      isSingleRun ||
      (isRunSelectorMinimized && !isTableExpanded)
    ) {
      setShouldShowRunSelector(false);
    } else if (!isLoading) {
      setShouldShowRunSelector(true);
    }
  }, [
    isFullScreenPanelNotFound,
    isLoading,
    isMobileWorkspaceTab,
    isSingleRun,
    setShouldShowRunSelector,
    isRunSelectorMinimized,
    isTableExpanded,
  ]);

  if (isLoading) {
    // We block the app until this is resolved. I's possible that the run selector
    // is ready to be rendered before this loader if finished. Consider moving it to
    // InstrumentedCenteredWaveLoader to help with perceived perf improvements.
    return <LoaderProxy name="multi-run-workspace-content" />;
  }

  if (isFullScreenPanelPage && !panelRefAndPanelBankSectionConfigRef) {
    return <FullScreenPanelNotFound />;
  }

  if (isSingleRunNotFound) {
    return <SingleRunNotFound />;
  }

  if (isFullScreenPanelPage && panelRefAndPanelBankSectionConfigRef) {
    // Render full screen panel within the run selector context to preserve state
    // when users navigate back from full screen view (avoiding full page reload)
    return (
      <SectionSettingsContextProvider
        sectionRef={
          panelRefAndPanelBankSectionConfigRef.panelBankSectionConfigRef
        }>
        <FullScreenPanelContainer
          isProjectReadOnly={isProjectReadOnly}
          panelRef={panelRefAndPanelBankSectionConfigRef.panelRef}
          panelBankSectionConfigRef={
            panelRefAndPanelBankSectionConfigRef.panelBankSectionConfigRef
          }
          isMobileWorkspaceTab={isMobileWorkspaceTab}
          isRunSelectorMinimized={isRunSelectorMinimized}
          toggleMinimizeRunSelectorWithTracking={
            toggleMinimizeRunSelectorWithTracking
          }
          className="h-full w-full"
        />
      </SectionSettingsContextProvider>
    );
  }

  if (children) {
    return <div style={{flexGrow: 1, overflow: 'auto'}}>{children}</div>;
  }

  if (isTableExpanded) {
    return null;
  }

  const panelBank = (
    <PanelBank
      entityName={entityName}
      projectName={projectName}
      isProjectReadOnly={isProjectReadOnly}
      isMobileWorkspaceTab={isMobileWorkspaceTab}
      workspaceID={workspaceID}
      isRunSelectorMinimized={isRunSelectorMinimized}
      toggleMinimizeRunSelectorWithTracking={
        toggleMinimizeRunSelectorWithTracking
      }
    />
  );

  if (isSingleRun) {
    return <RunDetails chartsContent={panelBank} workspaceID={workspaceID} />;
  }

  return panelBank;
};
